import {
  useCallback,
  useRef,
  useState,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from "react";
import {
  LIVE_FLASH_MS,
  LiveFlashKind,
  emptyLiveFlashes,
  type FlagLiveFlashInput,
  type LiveFlashes,
} from "@/types/frontend-types";
import type {
  FlagLiveFlash,
  IsInUserAttachmentsSubtree,
  LiveFlashPayload,
  LiveSignalTimer,
} from "./session-live-signals.types";

interface UseSessionLiveFlashesInput {
  initialLoadCompletedAtRef: MutableRefObject<number | null>;
  isInUserAttachmentsSubtree: IsInUserAttachmentsSubtree;
}

interface QueueLiveFlashTimeoutInput
  extends Pick<LiveFlashPayload, "kind" | "entityId"> {
  liveFlashTimeoutsRef: MutableRefObject<Record<string, LiveSignalTimer>>;
  setLiveFlashes: Dispatch<SetStateAction<LiveFlashes>>;
}

interface SessionLiveFlashSignals {
  liveFlashes: LiveFlashes;
  flagLiveFlash: FlagLiveFlash;
  canFlashForPoll: () => boolean;
  openInitialLoadGate: () => void;
  resetLiveFlashes: () => void;
  cleanupLiveFlashTimers: () => void;
}

export function useSessionLiveFlashes({
  initialLoadCompletedAtRef,
  isInUserAttachmentsSubtree,
}: UseSessionLiveFlashesInput): SessionLiveFlashSignals {
  const [liveFlashes, setLiveFlashes] = useState<LiveFlashes>(emptyLiveFlashes);
  const liveFlashTimeoutsRef = useRef<Record<string, LiveSignalTimer>>({});

  const flagLiveFlash = useCallback(
    (input: FlagLiveFlashInput): void => {
      if (shouldSkipLiveFlash({
        input,
        initialLoadCompletedAt: initialLoadCompletedAtRef.current,
        isInUserAttachmentsSubtree,
      })) return;

      const { kind, entityId, fields } = input;
      const flashedAt = Date.now();
      setLiveFlashes((prev) => selectMergedLiveFlashState({
        previous: prev, kind, entityId, fields, flashedAt,
      }));
      queueLiveFlashTimeout({ liveFlashTimeoutsRef, setLiveFlashes, kind, entityId });
    },
    [initialLoadCompletedAtRef, isInUserAttachmentsSubtree],
  );

  const canFlashForPoll = useCallback(
    (): boolean => initialLoadCompletedAtRef.current != null,
    [initialLoadCompletedAtRef],
  );

  const openInitialLoadGate = useCallback((): void => {
    initialLoadCompletedAtRef.current = Date.now();
  }, [initialLoadCompletedAtRef]);

  const resetLiveFlashes = useCallback((): void => {
    setLiveFlashes(emptyLiveFlashes());
  }, []);

  const cleanupLiveFlashTimers = useCallback((): void => {
    Object.values(liveFlashTimeoutsRef.current).forEach((timeout) => {
      clearTimeout(timeout);
    });
    liveFlashTimeoutsRef.current = {};
  }, []);

  return {
    liveFlashes,
    flagLiveFlash,
    canFlashForPoll,
    openInitialLoadGate,
    resetLiveFlashes,
    cleanupLiveFlashTimers,
  };
}

function shouldSkipLiveFlash(input: {
  input: FlagLiveFlashInput;
  initialLoadCompletedAt: number | null;
  isInUserAttachmentsSubtree: IsInUserAttachmentsSubtree;
}): boolean {
  if (input.initialLoadCompletedAt == null) return true;
  if (input.input.fields.length === 0) return true;

  const effectiveScope = selectEffectiveLiveFlashScope(input.input);
  if (!effectiveScope) return false;
  return input.isInUserAttachmentsSubtree(effectiveScope);
}

function selectEffectiveLiveFlashScope(
  input: FlagLiveFlashInput,
): string | undefined {
  return input.scopeProductId ??
    (input.kind === LiveFlashKind.Product ? input.entityId : undefined);
}

function selectMergedLiveFlashState(input: {
  previous: LiveFlashes;
  kind: LiveFlashKind;
  entityId: string;
  fields: readonly string[];
  flashedAt: number;
}): LiveFlashes {
  const prevByKind = input.previous[input.kind];
  const existingEntry = prevByKind[input.entityId];
  const mergedFields = existingEntry
    ? new Set(existingEntry.fields)
    : new Set<string>();
  input.fields.forEach((field) => mergedFields.add(field));

  return {
    ...input.previous,
    [input.kind]: {
      ...prevByKind,
      [input.entityId]: { fields: mergedFields, flashedAt: input.flashedAt },
    },
  };
}

function queueLiveFlashTimeout({
  liveFlashTimeoutsRef,
  setLiveFlashes,
  kind,
  entityId,
}: QueueLiveFlashTimeoutInput): void {
  const timeoutKey = selectLiveFlashTimeoutKey({ kind, entityId });
  const existingTimeout = liveFlashTimeoutsRef.current[timeoutKey];
  if (existingTimeout) {
    clearTimeout(existingTimeout);
  }

  liveFlashTimeoutsRef.current[timeoutKey] = setTimeout(() => {
    setLiveFlashes((prev) => selectLiveFlashStateAfterClear({
      previous: prev,
      kind,
      entityId,
    }));
    delete liveFlashTimeoutsRef.current[timeoutKey];
  }, LIVE_FLASH_MS);
}

function selectLiveFlashStateAfterClear(input: {
  previous: LiveFlashes;
  kind: LiveFlashKind;
  entityId: string;
}): LiveFlashes {
  const nextByKind = { ...input.previous[input.kind] };
  delete nextByKind[input.entityId];
  return { ...input.previous, [input.kind]: nextByKind };
}

function selectLiveFlashTimeoutKey({
  kind,
  entityId,
}: Pick<LiveFlashPayload, "kind" | "entityId">): string {
  return `${kind}::${entityId}`;
}
